import { useTracking } from '@repo/tracking/use-tracking';
import { Trans, useTranslation } from '@repo/translations/use-translation';
import { useState } from 'react';

import { useCartContext } from '@zooplus/cart-provider';
import { Anchor, ButtonAnchor, PrimaryButton } from '@zooplus/ui-components';

import { useAddMembershipToCart } from '../../../../hooks/useCart';
import { useMembershipOffer } from '../../../../hooks/useMembershipOffer';
import { MembershipsComparison } from '../../../../hooks/useMembershipsComparison';
import { PremiumAddedModal } from '../../../UI/PremiumAddedModal/PremiumAddedModal';
import { ProgramPlanBenefits } from '../ProgramPlanBenefits/ProgramPlanBenefits';
import { ProgramPlanHeader } from '../ProgramPlanHeader/ProgramPlanHeader';

import styles from './ProgramPlanContent.module.css';

export interface ProgramPlanContentProps {
  membershipOverview: MembershipsComparison;
  levelType: 'BASIC' | 'PREMIUM';
}

export const ProgramPlanContent = ({ membershipOverview, levelType }: ProgramPlanContentProps) => {
  const isLogged = !!membershipOverview.customerLoyaltyLevelType;
  const { t } = useTranslation();
  const { data: membership } = useMembershipOffer(isLogged);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddingMembership, setIsAddingMembership] = useState(false);
  const { cart, ready, sid } = useCartContext();
  const { addMembershipToCart } = useAddMembershipToCart();
  const { trackClick } = useTracking();

  const renderButton = (membershipOverview: MembershipsComparison, variant: typeof levelType) => {
    if (isLogged) {
      if (
        membershipOverview.customerLoyaltyLevelType === 'BASIC' &&
        variant === 'PREMIUM' &&
        membership !== undefined
      ) {
        return (
          <PrimaryButton
            data-testid={`upgrade-premium-button`}
            length="full"
            className={styles.joinButton}
            isLoading={isAddingMembership}
            onClick={async () => {
              trackClick({
                sid,
                target: {
                  name: 'plans_comparison',
                  id: 'upgrade_to_premium',
                },
              });

              setIsAddingMembership(true);
              try {
                await addMembershipToCart(membership?.offer.articleId);
                setIsModalOpen(true);
              } finally {
                setIsAddingMembership(false);
              }
            }}>
            {t('loyalty-program.public-landing-page.program-plans.buttons.upgrade-premium')}
          </PrimaryButton>
        );
      } else {
        return null;
      }
    } else if (variant === 'BASIC') {
      return (
        <ButtonAnchor
          onClick={() => {
            trackClick({
              sid,
              target: {
                name: 'plans_comparison',
                id: 'join_logged',
              },
            });
          }}
          href="/account/zooclub/dashboard"
          data-testid={`plan-sign-in-button`}
          length="full"
          className={styles.joinButton}>
          {t('loyalty-program.public-landing-page.program-plans.buttons.sign-in')}
        </ButtonAnchor>
      );
    } else {
      return (
        <ButtonAnchor
          onClick={() => {
            trackClick({
              sid,
              target: {
                name: 'plans_comparison',
                id: 'get_premium_logged',
              },
            });
          }}
          href="/account/zooclub/dashboard"
          data-testid={`get-premium-button`}
          length="full"
          className={styles.joinPremiumButton}>
          {t('loyalty-program.public-landing-page.program-plans.buttons.get-premium')}
        </ButtonAnchor>
      );
    }
  };

  return (
    <>
      <div className={styles.plan}>
        <ProgramPlanHeader membershipsComparison={membershipOverview} levelType={levelType} />
        <ProgramPlanBenefits levelType={levelType} membershipOverview={membershipOverview} />
        <div className={styles.buttonSection}>
          {ready ? renderButton(membershipOverview, levelType) : null}
          <span data-testid="terms-and-conditions" className={styles.termsAndConditions}>
            <Trans
              i18nKey={'loyalty-program.public-landing-page.program-plans.terms-and-conditions'}
              onClick={() => {
                trackClick({
                  sid,
                  target: {
                    name: 'terms_and_conditions',
                    id: 'terms_and_conditions_link',
                  },
                });
              }}
              components={{
                Link: (
                  <Anchor
                    className={styles.termsLink}
                    variant="neutral"
                    underline
                    isBold
                    href={
                      levelType === 'PREMIUM'
                        ? '/info/legal/club_premium_terms'
                        : '/info/legal/club_basic_terms'
                    }
                  />
                ),
              }}
            />
          </span>
        </div>
      </div>
      <PremiumAddedModal
        data-testid={'add-membership-modal'}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        isCartEmpty={ready && !!cart.articles && cart.articles.length == 0}
      />
    </>
  );
};
